/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  DatePicker,
  Input,
  Message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  TableColumnProps,
  Typography
} from '@arco-design/web-react';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import { get, post } from '../requestNew';
import './index.scss';
import type { BanListItem } from './types';

const TextArea = Input.TextArea;

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [20, 30, 50];

function BanList() {
  const [openIdFilter, setOpenIdFilter] = useState('');
  const [gameNameFilter, setGameNameFilter] = useState('');
  const [dataList, setDataList] = useState<BanListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [visibleBan, setVisibleBan] = useState(false);
  const [banUserOptions, setBanUserOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [banFetching, setBanFetching] = useState(false);
  const [banOpenId, setBanOpenId] = useState('');
  const [banReason, setBanReason] = useState('');
  const [banExpireTime, setBanExpireTime] = useState<string | undefined>(undefined);
  const banFetchIdRef = useRef(0);

  const getList = (opts: Record<string, any> = {}) => {
    setLoading(true);
    const params: any = {
      pageNum: opts.pageNum !== undefined ? opts.pageNum : pageNum,
      pageSize: opts.pageSize !== undefined ? opts.pageSize : pageSize
    };
    const openId = opts.openId !== undefined ? opts.openId : openIdFilter.trim();
    const gameName = opts.gameName !== undefined ? opts.gameName : gameNameFilter.trim();
    if (openId) params.openId = openId;
    if (gameName) params.gameName = gameName;

    get('/backend/ban/list', params)
      .then((res: any) => {
        if (res?.code === 200) {
          const data = res?.data ?? {};
          setDataList(data.list ?? data.records ?? []);
          setTotal(data.total ?? 0);
        } else {
          Message.error(res?.message || '查询失败');
        }
      })
      .catch(() => Message.error('网络错误'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getList();
  }, []);

  useEffect(() => {
    getList();
  }, [pageNum, pageSize]);

  const onFilterQuery = () => {
    setPageNum(1);
    getList({ pageNum: 1 });
  };

  const debouncedFetchUser = useCallback(
    debounce((gameName: string) => {
      banFetchIdRef.current = Date.now();
      const fetchId = banFetchIdRef.current;
      setBanFetching(true);
      setBanUserOptions([]);
      get('/backend/umsUser/list', { gameName, pageNum: 1, pageSize: 20 }).then((res: any) => {
        if (banFetchIdRef.current !== fetchId) return;
        setBanFetching(false);
        if (res?.code === 200) {
          const list = res?.data?.list || [];
          setBanUserOptions(
            list.map((item: any) => ({ label: item.gameName ?? item.openId, value: item.openId }))
          );
        }
      });
    }, 500),
    []
  );

  const openBanModal = () => {
    setVisibleBan(true);
    setBanOpenId('');
    setBanReason('');
    setBanExpireTime(undefined);
    setBanUserOptions([]);
  };

  const closeBanModal = () => {
    setVisibleBan(false);
    setBanOpenId('');
    setBanReason('');
    setBanExpireTime(undefined);
  };

  const submitBan = () => {
    if (!banOpenId) {
      Message.error('请选择用户');
      return;
    }
    const body: any = {
      openId: banOpenId,
      reason: banReason.trim() || undefined
    };
    if (banExpireTime) {
      body.expireTime = banExpireTime;
    }
    post('/backend/ban/ban', body).then((res: any) => {
      if (res?.code === 200) {
        Message.success('操作成功！');
        closeBanModal();
        getList({ pageNum: 1 });
        setPageNum(1);
      } else {
        Message.error(res?.message || '封号失败');
      }
    }).catch(() => Message.error('网络错误'));
  };

  const submitUnban = (openId: string) => {
    post('/backend/ban/unban', { openId }).then((res: any) => {
      if (res?.code === 200) {
        Message.success('操作成功！');
        getList();
      } else {
        Message.error(res?.message || '解封失败');
      }
    }).catch(() => Message.error('网络错误'));
  };

  const columns: TableColumnProps<BanListItem>[] = [
    {
      title: '参赛名',
      dataIndex: 'gameName',
      width: 140,
      render: (v) => v || '-'
    },
    {
      title: 'openId',
      dataIndex: 'openId',
      width: 220,
      render: (v) => (
        <Typography.Text copyable style={{ margin: 0 }}>
          {v}
        </Typography.Text>
      )
    },
    {
      title: '封号原因',
      dataIndex: 'reason',
      width: 220,
      render: (v) => v || '-'
    },
    {
      title: '操作人',
      dataIndex: 'operator',
      width: 120,
      render: (v) => v || '-'
    },
    {
      title: '解封时间',
      dataIndex: 'expireTime',
      width: 180,
      render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '永久')
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      width: 180,
      render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-')
    },
    {
      title: '操作',
      dataIndex: 'op',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Popconfirm title="确认解封该用户？" onOk={() => submitUnban(record.openId)}>
          <Button type="text" status="success" className="btn">
            解封
          </Button>
        </Popconfirm>
      )
    }
  ];

  return (
    <div className="ban-list table-flex">
      <Space wrap style={{ marginBottom: 12 }}>
        <Input
          className="filter-item"
          style={{ width: 200 }}
          allowClear
          placeholder="openId"
          value={openIdFilter}
          onChange={setOpenIdFilter}
        />
        <Input
          className="filter-item"
          style={{ width: 180 }}
          allowClear
          placeholder="参赛名"
          value={gameNameFilter}
          onChange={setGameNameFilter}
        />
        <Button type="primary" onClick={onFilterQuery}>
          查询
        </Button>
        <Button type="primary" status="danger" onClick={openBanModal}>
          封号
        </Button>
      </Space>

      <div className="table-scroll">
        <Spin loading={loading} style={{ display: 'block' }}>
          <Table
            rowKey="id"
            columns={columns}
            data={dataList}
            pagination={{
              total,
              current: pageNum,
              pageSize,
              sizeCanChange: true,
              sizeOptions: PAGE_SIZE_OPTIONS,
              showTotal: true,
              onChange: (page, size) => {
                setPageNum(page);
                setPageSize(size);
              }
            }}
            scroll={{ x: 1100 }}
          />
        </Spin>
      </div>

      <Modal
        title="封号"
        visible={visibleBan}
        onOk={submitBan}
        onCancel={closeBanModal}
        unmountOnExit
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>用户</div>
          <Select
            showSearch
            allowClear
            placeholder="输入参赛名搜索"
            style={{ width: '100%' }}
            filterOption={false}
            options={banUserOptions}
            value={banOpenId || undefined}
            onSearch={(v) => {
              if (v?.trim()) debouncedFetchUser(v.trim());
            }}
            onChange={(v) => setBanOpenId(v || '')}
            notFoundContent={banFetching ? <Spin size={16} /> : '暂无数据'}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>封号原因</div>
          <TextArea
            placeholder="请输入封号原因"
            value={banReason}
            onChange={setBanReason}
            maxLength={500}
            showWordLimit
          />
        </div>
        <div>
          <div style={{ marginBottom: 8 }}>解封时间（可选，不选为永久）</div>
          <DatePicker
            showTime
            style={{ width: '100%' }}
            value={banExpireTime ? dayjs(banExpireTime) : undefined}
            onChange={(dateString) => setBanExpireTime(dateString || undefined)}
            disabledDate={(current) => current && current.isBefore(dayjs().startOf('day'))}
          />
        </div>
      </Modal>
    </div>
  );
}

export default BanList;
